import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Check if a value should be considered empty or meaningless for display purposes.
 * Returns true for null, undefined, empty strings, "none", "-", "_", "n/a", NaN, etc.
 */
export function isEmptyOrMeaninglessValue(value: any, _type?: string): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'number' && isNaN(value)) return true;
  if (typeof value === 'string') {
    const trimmedValue = value.trim().toLowerCase();
    if (trimmedValue === '' || 
        trimmedValue === 'none' || 
        trimmedValue === '-' || 
        trimmedValue === '_' ||
        trimmedValue === '--' ||
        trimmedValue === 'n/a' ||
        trimmedValue === 'na' ||
        trimmedValue === 'null' ||
        trimmedValue === 'undefined' ||
        trimmedValue === 'not available' ||
        trimmedValue === 'not applicable' ||
        trimmedValue === 'unknown') {
      return true;
    }
    // score/percentage/number/duration features: models often return qualitative strings
    // ("High", "Moderate", "Strong CTA") instead of parseable numbers — still meaningful.
  }
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === 'object' && value !== null && Object.keys(value).length === 0) return true;
  return false;
}

const ANALYSIS_SECTION_MARKERS = [
  'Hook',
  'Hook_Opening',
  'Video_Understanding',
  'Overall_Verdict',
  'Retention_Pacing',
  'Format_Visual',
  'Predictions_Creator',
  'NLP_Transcript',
  'Predictions',
  'Consumer_Psychology',
  'Human_Demographics',
  'Scene_Time_Segments',
  'Product_Category_Type',
  'Platform_Fit',
  'Meta_Information',
  'Text_OCR',
  'TEXT_OCR',
  'Emotional_Journey',
  'Audience_Segmentation',
] as const;

const ANALYSIS_ENVELOPE_KEYS = new Set([
  'user_id',
  'video_name',
  'original_search',
  'metadata',
  'timestamp',
  'thumbnail_url',
  'filesize_bytes',
  'duration_seconds',
  'created',
  'updated',
]);

const ANALYSIS_META_TOP_LEVEL_KEYS = new Set([
  'Meta_Information',
  'metadata',
  'token_stats',
  'cost_optimization',
  'video_name',
  'video_info',
  'processing',
  'file_source',
]);

function isAnalysisRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isFilledMetricValue(value: unknown): boolean {
  return value !== null && value !== undefined && value !== '' && value !== false &&
    !(Array.isArray(value) && value.length === 0);
}

function hasAnalysisSections(obj: Record<string, unknown>): boolean {
  return ANALYSIS_SECTION_MARKERS.some((key) => {
    const section = obj[key];
    if (!isAnalysisRecord(section)) {
      return false;
    }
    return Object.values(section).some(isFilledMetricValue);
  });
}

function analysisPayloadFillScore(obj: Record<string, unknown>): number {
  let score = 0;
  for (const key of ANALYSIS_SECTION_MARKERS) {
    const section = obj[key];
    if (!isAnalysisRecord(section)) {
      continue;
    }
    for (const value of Object.values(section)) {
      if (isFilledMetricValue(value)) {
        score += 1;
      }
    }
  }
  return score;
}

/** Unwrap bucket-files API payloads into the flat schema for createFeatureCategories(). */
export function unwrapAnalysisPayload(raw: unknown): Record<string, any> {
  if (!isAnalysisRecord(raw)) {
    return {};
  }

  const root = raw as Record<string, any>;
  const candidates: Record<string, any>[] = [];

  if (isAnalysisRecord(root.content?.analysis_data)) {
    candidates.push(root.content.analysis_data);
  }
  if (isAnalysisRecord(root.analysis_data)) {
    candidates.push(root.analysis_data);
  }
  if (isAnalysisRecord(root.content)) {
    candidates.push(root.content);
  }
  if (isAnalysisRecord(root.content?.content)) {
    candidates.push(root.content.content);
  }
  candidates.push(root);

  const mergeRootMeta = (candidate: Record<string, any>) => {
    const merged = { ...candidate };
    if (root.video_name && !merged.video_name) {
      merged.video_name = root.video_name;
    }
    if (root.metadata && !merged.metadata) {
      merged.metadata = root.metadata;
    }
    return merged;
  };

  const rankedCandidates = candidates
    .filter(isAnalysisRecord)
    .map((candidate) => ({
      candidate,
      fillScore: analysisPayloadFillScore(candidate),
      hasSections: hasAnalysisSections(candidate),
    }))
    .filter((entry) => entry.fillScore > 0 || entry.hasSections)
    .sort((a, b) => b.fillScore - a.fillScore);

  if (rankedCandidates.length > 0) {
    return mergeRootMeta(rankedCandidates[0].candidate);
  }

  const byKeyCount = candidates
    .filter(isAnalysisRecord)
    .sort((a, b) => Object.keys(b).length - Object.keys(a).length);
  return mergeRootMeta(byKeyCount[0] ?? root);
}

export type VideoNameSource = {
  video_name?: string | null;
  file_name?: string | null;
  name?: string | null;
  alias_name?: string | null;
  blob_path?: string | null;
  video_id?: string | null;
};

function trimVideoName(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** Resolve the storage key used for bucket-files API calls and exports. */
export function resolveVideoStorageName(
  source: VideoNameSource,
  userId?: string
): string {
  const direct =
    trimVideoName(source.video_name) ||
    trimVideoName(source.file_name) ||
    trimVideoName(source.name) ||
    trimVideoName(source.video_id);
  if (direct) return direct;

  const blobPath = trimVideoName(source.blob_path);
  if (blobPath) {
    const filename = blobPath.split('/').pop() || '';
    if (filename.startsWith('final_result_') && filename.endsWith('.json')) {
      let stem = filename.slice('final_result_'.length, -'.json'.length);
      if (userId && stem.startsWith(`${userId}_`)) {
        stem = stem.slice(userId.length + 1);
      } else {
        const prefixed = stem.match(/^[^_]+_(.+)$/);
        if (prefixed?.[1]) stem = prefixed[1];
      }
      if (stem) return stem;
    }
    if (filename) {
      return filename.replace(/\.(json|mp4)$/i, '');
    }
  }

  return trimVideoName(source.alias_name);
}

/** Count non-empty PascalCase schema sections (detect incomplete loads after redirect). */
export function countFilledAnalysisSections(data: Record<string, any>): number {
  return Object.entries(data).filter(([key, value]) => {
    if (!/^[A-Z][A-Za-z0-9_]*$/.test(key)) return false;
    if (ANALYSIS_ENVELOPE_KEYS.has(key) || ANALYSIS_META_TOP_LEVEL_KEYS.has(key)) return false;
    if (!isAnalysisRecord(value)) return false;
    return Object.keys(value).length > 0;
  }).length;
}
